import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { commentVisualWidth, injectMarquee } from '../../../components/CommentPreviewButton';
import type { DirectorNoteItem } from '../../../types';

// 与 CommentPreviewButton（查看新评论）的 md 形态视觉对齐，仅配色改为浅绿；
// 点击后打开与评论一致的居中弹窗，逐条展示导演提示（含标注作者）。
const PREVIEW_CH = 20;
const GREEN_BG = '#dcfce7';
const GREEN_BORDER = '#bbf7d0';

interface DirectorNotesProps {
  notes: DirectorNoteItem[];
}

export default function DirectorNotes({ notes }: DirectorNotesProps) {
  const { t } = useTranslation('episodeDetail');
  const [open, setOpen] = useState(false);
  if (notes.length === 0) return null;

  const text = notes.map(note => note.text).join('；');
  const visualWidth = commentVisualWidth(text);
  const needsScroll = visualWidth > PREVIEW_CH - 2;
  if (needsScroll) injectMarquee();

  return (
    <>
      <button
        type="button"
        title={t('directorMapping.notesTitle')}
        onClick={event => { event.stopPropagation(); setOpen(true); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 0,
          background: GREEN_BG, color: '#000', border: `1px solid ${GREEN_BORDER}`, borderRadius: '8px',
          fontSize: '0.85rem', cursor: 'pointer', padding: 0, overflow: 'hidden', flexShrink: 0, height: '36px',
        }}
      >
        <span style={{
          padding: '0 0.75rem', whiteSpace: 'nowrap', fontWeight: 600, height: '100%',
          borderRight: `1px solid ${GREEN_BORDER}`,
          display: 'flex', alignItems: 'center', gap: '0.3rem',
        }}>
          {t('directorMapping.notesTitle')}
          <span style={{
            background: '#ef4444', color: '#fff', fontSize: '0.78rem', fontWeight: 700,
            padding: '0 0.4rem', borderRadius: '9999px', lineHeight: '1.4', minWidth: '1.2rem', textAlign: 'center',
          }}>
            {notes.length}
          </span>
        </span>
        <span style={{ width: `${PREVIEW_CH}ch`, overflow: 'hidden', padding: '0 0.5rem', display: 'flex', alignItems: 'center' }}>
          {needsScroll ? (
            <span style={{
              display: 'inline-block', whiteSpace: 'nowrap', paddingLeft: `${PREVIEW_CH}ch`,
              animation: `comment-preview-scroll ${Math.max(visualWidth * 0.3, 4)}s linear infinite`,
            }}>
              {text}
            </span>
          ) : (
            <span style={{ whiteSpace: 'nowrap' }}>{text}</span>
          )}
        </span>
      </button>

      {open && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000 }}
            onClick={event => { event.stopPropagation(); setOpen(false); }}
          />
          <div
            onClick={event => event.stopPropagation()}
            style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: '960px', maxWidth: '90vw', maxHeight: '80vh',
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px',
              display: 'flex', flexDirection: 'column', zIndex: 1001, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>{t('directorMapping.notesTitle')} ({notes.length})</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-2)', padding: '0.25rem' }}
              >
                ✕
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
              {notes.map((note, index) => (
                <div
                  key={`${index}:${note.text}`}
                  style={{
                    marginBottom: '1rem',
                    borderLeft: '3px solid var(--status-completed, #22c55e)',
                    paddingLeft: '0.75rem',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{t('directorMapping.noteItem', { index: index + 1 })}</span>
                    {note.authorName && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{note.authorName}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.88rem', background: 'var(--surface-2)', padding: '0.5rem 0.75rem', borderRadius: '6px', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {note.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}
